import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, CircularProgress, Divider } from '@material-ui/core'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useChartColors } from '../../../../charts/echarts/ColorProvider'
import { COLUMN_CATALOG_MAP } from '../../lib/constants'
import { effectiveFormat } from '../../lib/effectiveFormat'
import { usePortfolioData } from '../PortfolioDataProvider'
import { useUtilityPanel } from '../UtilityPanelProvider'
import { buildLevelRequest, buildRowRequest } from './trend/buildTrendRequest'
import { useTrendQuery } from './trend/useTrendQuery'
import { loadChartType, saveChartType } from './trend/chartTypePreference'
import { loadFrequency, saveFrequency } from './trend/frequencyPreference'
import ChartTypeToggle from './trend/ChartTypeToggle'
import FrequencyPicker from './trend/FrequencyPicker'
import TrendLineChart from './trend/TrendLineChart'
import TrendBarChart from './trend/TrendBarChart'
import TrendDetailTable from './trend/TrendDetailTable'
import SeriesChipStrip from './trend/SeriesChipStrip'
import CompareMenu from './trend/CompareMenu'
import { canAddSeriesCandidate, isSeriesEligible, DEFAULT_MAX_SERIES } from './trend/seriesCompatibility'
import { activeRowKeyForScope } from './trend/rowKey'

const CHART_COMPONENTS = {
  line: TrendLineChart,
  bar: TrendBarChart
}

const useStyles = makeStyles(() => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 14
  },
  chipBar: {
    display: 'flex',
    alignItems: 'center'
  },
  loading: {
    flex: 1,
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  error: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    textAlign: 'center',
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 13
  },
  empty: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4
  }
}))

function findColumnDefinition (columnDefinitions, columnInstance) {
  if (!columnInstance) return null
  return (columnDefinitions || []).find(d => d.accessorKey === columnInstance.id) || null
}

// Stable per-series key. Pinned to scope + row identity + columnInstance.id
// so adding the same comparison twice (which is blocked anyway) wouldn't
// produce duplicate keys, and reordering the array preserves identity
// across renders.
function seriesKeyFor (scope, row, columnInstance) {
  const rowKey = row?.groupEntityId != null ? `id:${row.groupEntityId}` : (row?.name ?? '')
  return `${scope}|${rowKey}|${columnInstance?.id ?? ''}`
}

function makeSeriesEntry (scope, row, columnInstance) {
  return {
    key: seriesKeyFor(scope, row, columnInstance),
    scope,
    row: row || null,
    columnInstance,
    // null = inherit chart-type default (line -> cumulative, bar ->
    // perPeriod). Explicit values come from the chip kebab menu and
    // survive chart-type toggles.
    mode: null
  }
}

// chartType -> the "auto" mode that applies to any series whose
// per-series mode is null. Single source of truth so the request
// builder, chip strip, and detail table all stay in sync.
function chartTypeDefaultMode (chartType) {
  return chartType === 'line' ? 'cumulative' : 'perPeriod'
}

function resolveEffectiveMode (entryMode, chartType) {
  return entryMode || chartTypeDefaultMode(chartType)
}

// "Reset to primary" check -- when the payload changes the user is
// looking at a new cell, so blow away comparisons. Compare on the
// derived key so payload churn that doesn't change identity (e.g.
// asOfDate refresh) doesn't drop user-added comparisons.
function payloadIdentity (payload) {
  if (!payload?.columnInstance) return null
  return seriesKeyFor(payload.scope, payload.row, payload.columnInstance)
}

// Catalog presets seed compares via `payload.compareColumns`. No
// compatibility check -- resolvers are trusted to pre-validate.
function seriesFromPayload (payload) {
  if (!payload?.columnInstance) return []
  const primary = makeSeriesEntry(payload.scope, payload.row, payload.columnInstance)
  const compares = Array.isArray(payload.compareColumns) ? payload.compareColumns : []
  const out = [primary]
  for (const candidate of compares) {
    if (!candidate?.id) continue
    if (out.some(s => s.columnInstance?.id === candidate.id)) continue
    out.push(makeSeriesEntry(payload.scope, payload.row, candidate))
  }
  return out
}

export default function TrendTool ({ payload, compact, onPayloadChange }) {
  const classes = useStyles()
  const { userId } = useAppContext()
  const {
    config,
    asOfDate,
    columnDefinitions,
    prefs,
    clientId,
    entity,
    entityIds,
    selectedSubject
  } = usePortfolioData()
  // setToolBridge is shared with the grid for Pattern B `Add to
  // current trend`; not per-tile.
  const { setToolBridge } = useUtilityPanel()

  // ECharts palette colors -- read here so we can mirror the chart's
  // color assignment on each SeriesChip swatch. Sliced/cycled by
  // series index, just like ECharts does internally.
  const paletteColors = useChartColors('default')

  // chartType / frequency: payload.* wins (per-instance persistence).
  // When unset, fall back to the user's global localStorage pref.
  // User changes write to BOTH the global pref (so new instances
  // inherit) AND the instance payload (so this tile remembers across
  // reloads). Local state covers the transient-tile case where
  // there's no payload to persist into.
  const [localChartType, setLocalChartType] = useState(() => payload?.chartType || loadChartType(userId))
  const chartType = payload?.chartType || localChartType
  const handleChartTypeChange = useCallback((next) => {
    setLocalChartType(next)
    saveChartType(userId, next)
    if (onPayloadChange) onPayloadChange({ chartType: next })
  }, [userId, onPayloadChange])

  const [localFrequency, setLocalFrequency] = useState(() => payload?.frequency || loadFrequency(userId))
  const frequency = payload?.frequency || localFrequency
  const handleFrequencyChange = useCallback((next) => {
    setLocalFrequency(next)
    saveFrequency(userId, next)
    if (onPayloadChange) onPayloadChange({ frequency: next })
  }, [userId, onPayloadChange])

  // -- returnStyle (per-series) --
  // cumulative is now a per-series setting. Each entry's `mode` is
  // either explicit ('cumulative' / 'perPeriod') or null (= follow the
  // chart-type default). buildLevelRequest / buildRowRequest accept a
  // `returnStyleFor(instance, i) => returnStyle` factory so each emitted
  // Orion calc carries its own returnStyle.cumulative.
  const buildReturnStyleForEntry = useCallback((entry) => ({
    frequency,
    basedOn: 'Calendar',
    breakBehavior: 'AllowPartialPeriods',
    cumulative: resolveEffectiveMode(entry?.mode, chartType) === 'cumulative',
    context: 'Self'
  }), [frequency, chartType])

  // Primary at index 0. `payload.compareColumns` (catalog) seeds
  // initial compares; `+ Compare` / Pattern B appends at runtime.
  const [series, setSeries] = useState(() => seriesFromPayload(payload))
  const [detailSeriesKey, setDetailSeriesKey] = useState(() => {
    if (!payload?.columnInstance) return null
    return seriesKeyFor(payload.scope, payload.row, payload.columnInstance)
  })

  // Reset series + detail picker when the user opens trend on a fresh
  // cell. Identity-based -- payload churn that doesn't actually swap
  // the primary measure/row keeps existing comparisons. Done via the
  // "previous prop key" render-phase pattern rather than `useEffect`
  // so the stale array reference never gets committed to the DOM; on
  // a payload identity flip React discards the in-progress render and
  // re-renders with the fresh primary.
  //
  // See React docs "Adjusting some state when a prop changes":
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const payloadKey = payloadIdentity(payload)
  const [prevPayloadKey, setPrevPayloadKey] = useState(payloadKey)
  if (payloadKey !== prevPayloadKey) {
    setPrevPayloadKey(payloadKey)
    if (!payload?.columnInstance) {
      setSeries([])
      setDetailSeriesKey(null)
    } else {
      const next = seriesFromPayload(payload)
      setSeries(next)
      setDetailSeriesKey(next[0]?.key || null)
    }
  }

  const primary = series[0] || null
  const columnInstancesForRequest = useMemo(() => series.map(s => s.columnInstance), [series])

  // -- request synthesis --
  // returnStyleFor closes over `series` so each emitted calc gets the
  // matching entry's effective cumulative mode. The factory is
  // recomputed whenever series / chartType / frequency change, which is
  // also exactly the set of inputs that should re-fire the query.
  const returnStyleFor = useCallback((_instance, i) => {
    return buildReturnStyleForEntry(series[i])
  }, [series, buildReturnStyleForEntry])

  // Subject wins over the primary's catalog scope.
  //
  // Trend is single-entity by nature, so a level subject ("All Products")
  // degrades to the household trend -- Trend's buildLevelRequest already
  // builds at the household level (no per-level fan-out). A future
  // iteration could overlay one line per entity at the level.
  const built = useMemo(() => {
    if (!primary || !config || !asOfDate || !entity || !entityIds) return null
    if (selectedSubject?.kind === 'row') {
      return buildRowRequest({
        config,
        asOfDate,
        entity,
        entityIds,
        row: selectedSubject.row,
        columnInstances: columnInstancesForRequest,
        returnStyleFor
      })
    }
    if (selectedSubject?.kind === 'level') {
      return buildLevelRequest({
        config,
        asOfDate,
        entity,
        entityIds,
        columnInstances: columnInstancesForRequest,
        returnStyleFor
      })
    }
    if (primary.scope === 'level') {
      return buildLevelRequest({
        config,
        asOfDate,
        entity,
        entityIds,
        columnInstances: columnInstancesForRequest,
        returnStyleFor
      })
    }
    if (primary.scope === 'row') {
      return buildRowRequest({
        config,
        asOfDate,
        entity,
        entityIds,
        row: primary.row,
        columnInstances: columnInstancesForRequest,
        returnStyleFor
      })
    }
    return null
  }, [primary, columnInstancesForRequest, config, asOfDate, entity, entityIds, returnStyleFor, selectedSubject])

  const request = built?.request || null
  const unwrap = built?.unwrap || null
  // Memoize calcIds so downstream useMemo deps don't see a fresh
  // array reference on every render -- `built?.calcIds || []` would
  // otherwise return a brand-new `[]` literal each render whenever
  // the request hasn't been built yet, invalidating resolvedSeries
  // and resolvedFrequency's memos.
  const calcIds = useMemo(() => built?.calcIds || [], [built])

  const { seriesByCalcId, isFetching, error } = useTrendQuery({
    request,
    clientId,
    unwrap
  })

  // -- per-series resolution --
  // Joins our `series` state with the query result + grid column defs
  // into the shape both the chart and detail table consume. One pass
  // so chips, chart, and detail all stay in sync.
  const resolvedSeries = useMemo(() => series.map((s, idx) => {
    const calcId = calcIds[idx] || null
    const queryEntry = calcId ? seriesByCalcId[calcId] : null
    const columnDef = findColumnDefinition(columnDefinitions, s.columnInstance)
    // Fall back to catalog metadata for catalog-injected columns the user
    // hasn't added to their visible column set.
    const catalog = COLUMN_CATALOG_MAP[s.columnInstance.columnType] || null
    const fmt = columnDef?.format ?? catalog?.format
    const measureKind = columnDef?.measureKind ?? catalog?.measureKind
    const valueFormat = fmt ? effectiveFormat(fmt, measureKind, prefs) : null
    const color = paletteColors?.[idx % (paletteColors.length || 1)] || null
    const name = columnDef?.header || catalog?.label || s.columnInstance.columnType
    const effectiveMode = resolveEffectiveMode(s.mode, chartType)
    return {
      key: s.key,
      id: s.key,
      name,
      color,
      valueFormat,
      seriesType: queryEntry?.seriesType || null,
      points: queryEntry?.points || [],
      hasData: !!queryEntry?.points?.length,
      columnInstance: s.columnInstance,
      row: s.row,
      scope: s.scope,
      mode: s.mode || null,
      effectiveMode
    }
  }), [series, calcIds, seriesByCalcId, columnDefinitions, prefs, paletteColors, chartType])

  const primaryResolved = resolvedSeries[0] || null
  // Resolved frequency reads off any one of the returned series --
  // they share frequency (single returnStyle, single request).
  const resolvedFrequency = useMemo(() => {
    for (const calcId of calcIds) {
      const entry = seriesByCalcId[calcId]
      if (entry?.resolvedFrequency) return entry.resolvedFrequency
    }
    return null
  }, [calcIds, seriesByCalcId])

  // Push the compares (everything after primary at index 0) into the
  // instance payload as `compareColumns`. We persist just the column
  // instances -- per-series cumulative mode is a session-level
  // affordance for v1 and isn't round-tripped.
  const persistCompares = useCallback((nextSeries) => {
    if (!onPayloadChange) return
    const compares = (nextSeries || []).slice(1).map(s => s.columnInstance)
    onPayloadChange({ compareColumns: compares })
  }, [onPayloadChange])

  // -- multi-series add/remove/mode handlers --
  const removeSeries = useCallback((key) => {
    let snapshot = null
    setSeries(prev => {
      const filtered = prev.filter(s => s.key !== key)
      if (filtered.length === prev.length) return prev
      snapshot = filtered
      return filtered
    })
    setDetailSeriesKey(prev => prev === key ? null : prev)
    if (snapshot) persistCompares(snapshot)
  }, [persistCompares])

  // Sets a specific series's cumulative mode. No-op when the value
  // hasn't actually changed (avoids a redundant request fire when the
  // user clicks the already-active menu item). Session-only; not
  // persisted in v1.
  const setSeriesMode = useCallback((key, nextMode) => {
    setSeries(prev => {
      let touched = false
      const next = prev.map(s => {
        if (s.key !== key) return s
        if ((s.mode || null) === (nextMode || null)) return s
        touched = true
        return { ...s, mode: nextMode || null }
      })
      return touched ? next : prev
    })
  }, [])

  const addSeries = useCallback((candidateColumnInstance) => {
    if (!primary || !candidateColumnInstance) return false
    let snapshot = null
    setSeries(prev => {
      const decision = canAddSeriesCandidate({
        primary: prev[0]?.columnInstance,
        currentSeries: prev,
        candidate: candidateColumnInstance,
        columnDefinitions,
        max: DEFAULT_MAX_SERIES
      })
      if (!decision.ok) return prev
      // New comparisons inherit the primary's scope/row -- v1 is
      // same-row only (no cross-row comparisons yet).
      const entry = makeSeriesEntry(prev[0].scope, prev[0].row, candidateColumnInstance)
      const next = [...prev, entry]
      snapshot = next
      return next
    })
    if (snapshot) persistCompares(snapshot)
    return true
  }, [primary, columnDefinitions, persistCompares])

  const canAddSeries = useCallback((candidateColumnInstance) => {
    if (!primary || !candidateColumnInstance) return { ok: false, reason: 'No primary series' }
    return canAddSeriesCandidate({
      primary: primary.columnInstance,
      currentSeries: series,
      candidate: candidateColumnInstance,
      columnDefinitions,
      max: DEFAULT_MAX_SERIES
    })
  }, [primary, series, columnDefinitions])

  // -- candidate pool for CompareMenu --
  // Restrict to the user's currently-configured grid columns (same
  // pool the allocation tool's measure picker uses). Each candidate
  // carries its columnInstance + display label; CompareMenu computes
  // eligibility per-item.
  const candidates = useMemo(() => {
    const defsById = new Map((columnDefinitions || []).map(d => [d.accessorKey, d]))
    const out = []
    for (const col of (config?.columns || [])) {
      if (!isSeriesEligible(col)) continue
      const def = defsById.get(col.id)
      const label = def?.header || col.columnType
      out.push({ columnInstance: col, label })
    }
    return out
  }, [config?.columns, columnDefinitions])

  // -- publish bridge for the grid's Pattern B `Add to current trend` --
  // Grid reads this through useUtilityPanel(). Refreshed on every
  // change to series/columnDefinitions so the eligibility decision
  // tracks the current series count.
  useEffect(() => {
    if (!primary) {
      setToolBridge(null)
      return
    }
    const primaryDef = findColumnDefinition(columnDefinitions, primary.columnInstance)
    setToolBridge({
      kind: 'trend',
      activeRowKey: activeRowKeyForScope(primary.scope, primary.row),
      primaryMeasureKind: primaryDef?.measureKind || null,
      canAddSeries,
      addSeries
    })
  }, [primary, columnDefinitions, canAddSeries, addSeries, setToolBridge])

  // Clear bridge on unmount so a stale descriptor doesn't outlive the
  // tool (the panel provider also clears it on closeTool, this is the
  // belt-and-suspenders cleanup for tool unmount within an open panel).
  useEffect(() => {
    return () => setToolBridge(null)
  }, [setToolBridge])

  // -- render --
  // Toolbar + series chip strip always render so the user can swap
  // chart type, frequency, and series even when the current state
  // produced no chart (no primary, request invalid, error, etc.).
  const Chart = CHART_COMPONENTS[chartType] || TrendLineChart
  const displayFrequency = resolvedFrequency || frequency
  const chipItems = resolvedSeries.map(s => ({
    id: s.id,
    label: s.name,
    color: s.color,
    hint: !s.hasData && !isFetching ? 'no data' : null,
    mode: s.mode || undefined,
    effectiveMode: s.effectiveMode
  }))

  let chartContent = null
  if (!payload?.columnInstance) {
    chartContent = (
      <div className={classes.empty}>
        Right-click a chartable column header or cell to view its trend over time.
      </div>
    )
  } else if (!request) {
    chartContent = (
      <div className={classes.empty}>
        This measure has no trend available.
      </div>
    )
  } else if (error) {
    chartContent = (
      <div className={classes.error}>
        <span>Could not load trend.</span>
        <span>{error?.message || 'Unknown error'}</span>
      </div>
    )
  } else if (isFetching && resolvedSeries.every(s => !s.hasData)) {
    chartContent = (
      <div className={classes.loading}>
        <CircularProgress size={18} />
        <span>Loading trend...</span>
      </div>
    )
  } else {
    chartContent = (
      <>
        <Chart
          seriesList={resolvedSeries}
          valueFormat={primaryResolved?.valueFormat || null}
          frequency={displayFrequency}
          height={340}
        />
        {!compact && (
          <TrendDetailTable
            seriesList={resolvedSeries}
            frequency={displayFrequency}
            detailSeriesKey={detailSeriesKey || primaryResolved?.id}
            onDetailSeriesChange={setDetailSeriesKey}
          />
        )}
      </>
    )
  }

  return (
    <div className={classes.body}>
      <div className={classes.toolbar}>
        <ChartTypeToggle value={chartType} onChange={handleChartTypeChange} />
        <Divider orientation='vertical' flexItem style={{ margin: '0 6px' }} />
        <FrequencyPicker value={frequency} onChange={handleFrequencyChange} />
      </div>
      <SeriesChipStrip
        items={chipItems}
        onRemove={removeSeries}
        onModeChange={setSeriesMode}
        trailing={
          <CompareMenu
            candidates={candidates}
            primary={primary?.columnInstance}
            currentSeries={series}
            columnDefinitions={columnDefinitions}
            max={DEFAULT_MAX_SERIES}
            onAdd={addSeries}
          />
        }
      />
      {chartContent}
    </div>
  )
}

TrendTool.propTypes = {
  compact: PropTypes.bool,
  onPayloadChange: PropTypes.func,
  payload: PropTypes.shape({
    scope: PropTypes.oneOf(['level', 'row']).isRequired,
    columnInstance: PropTypes.object,
    row: PropTypes.object,
    depth: PropTypes.number,
    groupingLabel: PropTypes.string,
    compareColumns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      columnType: PropTypes.string
    })),
    chartType: PropTypes.oneOf(['line', 'bar']),
    frequency: PropTypes.string
  })
}
